import React, { useState } from 'react';
import Loader from 'components/atoms/loader';
import AiManagementFilePreview from 'components/atoms/ai_management_file_preview';
import StatusFilterBadge from 'components/atoms/status_filter_badge';
import { BidderFile } from 'hooks/useBidderFiles';
import { biddingFileHref } from 'services/actions/ai_management_files';
import { formatDateTimeLong } from 'services/ai_management_dates';
import { Container } from './styles';

interface AiManagementBidderFilesProps {
  files: BidderFile[];
  loading: boolean;
  error: boolean;
}

const AiManagementBidderFiles: React.FC<AiManagementBidderFilesProps> = ({
  files,
  loading,
  error,
}) => {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="py-4">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-warning mb-0" role="alert">
        Não foi possível carregar os arquivos gerados pelo licitante.
      </div>
    );
  }

  if (!files.length) {
    return <div className="text-muted">Nenhum arquivo do licitante para esta licitação.</div>;
  }

  return (
    <Container>
      <div className="files-card card border">
        <table className="table mb-0">
          <thead className="thead-light">
            <tr>
              <th scope="col">Nome do arquivo</th>
              <th scope="col">Data</th>
              <th scope="col" aria-label="Ações" />
            </tr>
          </thead>
          <tbody>
            {files.map((file) => {
              const ready = file.status === 'completed';
              const expanded = ready && expandedKey === file.key;
              return (
                <React.Fragment key={file.key}>
                  <tr>
                    <td className="file-name align-middle">
                      {file.name}
                      {file.generation === 'latest' && (
                        <span className="badge badge-success ml-2">Última proposta gerada</span>
                      )}
                      {file.generation === 'previous' && (
                        <span className="badge badge-light border ml-2">Versão anterior</span>
                      )}
                      <small className="file-origin d-block text-muted">
                        {file.source === 'preparar_documentacao'
                          ? `Adicionado em: ${file.sourceLabel}${
                              file.requirementTitle ? ` · ${file.requirementTitle}` : ''
                            }`
                          : `Gerado em: ${file.sourceLabel}`}
                      </small>
                    </td>
                    <td className="align-middle text-nowrap">
                      {file.generatedAt ? formatDateTimeLong(file.generatedAt) : '—'}
                    </td>
                    <td className="file-actions align-middle text-right text-nowrap">
                      {ready ? (
                        <>
                          <a
                            className="btn btn-outline-secondary btn-sm mr-1"
                            href={biddingFileHref(file.downloadUrl)}
                            download={file.name}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={`Baixar ${file.name}`}
                          >
                            <i className="material-icons">file_download</i>
                          </a>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setExpandedKey(expanded ? null : file.key)}
                            aria-expanded={expanded}
                            aria-label={
                              expanded ? 'Fechar pré-visualização' : 'Pré-visualizar arquivo'
                            }
                          >
                            <i className="material-icons">
                              {expanded ? 'expand_less' : 'expand_more'}
                            </i>
                          </button>
                        </>
                      ) : (
                        <div className="d-flex justify-content-end">
                          <StatusFilterBadge status={file.status} />
                        </div>
                      )}
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="file-preview-row">
                      <td colSpan={3} className="px-3 pb-3 pt-0">
                        <div className="file-preview-box d-flex flex-column">
                          <AiManagementFilePreview
                            name={file.name}
                            downloadUrl={file.downloadUrl}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Container>
  );
};

export default AiManagementBidderFiles;
